import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import {
    salvarMarketingAutomatico,
    type MarketingAutomaticoItem,
    type SalvarMarketingPayload,
} from "../../../services/marketingAutomatico"
import { MarketingAutomaticoActionsTypes } from "../context/marketingAutomaticoActions"
import { useMarketingAutomaticoContext } from "../context/useMarketingAutomaticoContext"

const EVENTOS = [
    { value: "CADASTRO_SEM_PEDIDO", label: "Cadastro sem pedido" },
    { value: "AUSENCIA", label: "Ausência" },
    { value: "ANIVERSARIO", label: "Aniversário" },
    { value: "CADASTRO", label: "Cadastro" },
    { value: "BOAS_VINDAS", label: "Boas-vindas" },
    { value: "DIARIAMENTE", label: "Diariamente" },
]

const DIAS_SEMANA = [
    { value: "0", label: "Todos os dias" },
    { value: "2", label: "Segunda" },
    { value: "3", label: "Terça" },
    { value: "4", label: "Quarta" },
    { value: "5", label: "Quinta" },
    { value: "6", label: "Sexta" },
    { value: "7", label: "Sábado" },
    { value: "1", label: "Domingo" },
]

const EVENTOS_SEM_DIAS = ["DIARIAMENTE", "ANIVERSARIO"]

const useStyles = makeStyles(theme => ({
    dialog: { "& .MuiDialog-paper": { borderRadius: 16, maxWidth: 640, width: "100%" } },
    titulo: { fontWeight: 700, color: "#2c3e50" },
    secao: {
        fontWeight: 600,
        color: "#4caf50",
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    botaoSalvar: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "#fff",
        fontWeight: 600,
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    botaoCancelar: { borderRadius: 25, marginRight: theme.spacing(1) },
}))

type FormState = {
    id: number | null
    evento: string
    tempoPosEvento: string
    tempoFinal: string
    diaParaEnviar: string
    hora: string
    tituloMensagem: string
    descricaoMensagem: string
    criarCupom: boolean
    nomeCupom: string
    diasValidade: string
    tipoDesconto: string
    valorDesconto: string
    valorMinimo: string
}

const formInicial: FormState = {
    id: null,
    evento: "CADASTRO_SEM_PEDIDO",
    tempoPosEvento: "",
    tempoFinal: "",
    diaParaEnviar: "0",
    hora: new Date().toTimeString().substring(0, 5),
    tituloMensagem: "",
    descricaoMensagem: "",
    criarCupom: false,
    nomeCupom: "",
    diasValidade: "",
    tipoDesconto: "",
    valorDesconto: "",
    valorMinimo: "",
}

function itemParaForm(item: MarketingAutomaticoItem): FormState {
    return {
        id: item.id,
        evento: item.evento,
        tempoPosEvento: item.tempoPosEvento != null ? String(item.tempoPosEvento) : "",
        tempoFinal: item.tempoFinal != null ? String(item.tempoFinal) : "",
        diaParaEnviar: String(item.diaParaEnviar ?? 0),
        hora: item.horaDisparo ?? "",
        tituloMensagem: item.tituloMensagem,
        descricaoMensagem: item.descricaoMensagem,
        criarCupom: item.criarCupom,
        nomeCupom: item.nomeCupom ?? "",
        diasValidade: item.diasValidade != null ? String(item.diasValidade) : "",
        tipoDesconto: item.tipoDesconto != null ? String(item.tipoDesconto) : "",
        valorDesconto: item.valorDesconto != null ? String(item.valorDesconto) : "",
        valorMinimo: item.valorMinimo != null ? String(item.valorMinimo) : "",
    }
}

interface Props {
    aberto: boolean
    itemEditando: MarketingAutomaticoItem | null
    onFechar: () => void
}

export function FormMarketingAutomatico({ aberto, itemEditando, onFechar }: Props) {
    const classes = useStyles()
    const { dispatch } = useMarketingAutomaticoContext()
    const { enqueueSnackbar } = useSnackbar()
    const [form, setForm] = useState<FormState>(formInicial)
    const [salvando, setSalvando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (aberto) {
            setForm(
                itemEditando
                    ? itemParaForm(itemEditando)
                    : { ...formInicial, hora: new Date().toTimeString().substring(0, 5) }
            )
        }
    }, [aberto, itemEditando])

    const semDias = EVENTOS_SEM_DIAS.includes(form.evento)

    function atualizarCampo(campo: keyof FormState, valor: string | boolean) {
        setForm(prev => ({ ...prev, [campo]: valor }))
    }

    async function handleSalvar() {
        if (!form.tituloMensagem.trim()) {
            alertStart("Título da mensagem é obrigatório.", "error")
            return
        }
        if (form.descricaoMensagem.trim().length < 3) {
            alertStart("Descrição da mensagem é obrigatória.", "error")
            return
        }
        if (form.criarCupom) {
            if (!form.nomeCupom.trim()) {
                alertStart("O campo nome do Cupom deve ser preenchido!", "error")
                return
            }
            if (!form.diasValidade) {
                alertStart("A quantidade de dias de validade do Cupom deve ser informada!", "error")
                return
            }
            if (Number(form.diasValidade) < 0) {
                alertStart("A quantidade de dias não pode ser negativa!", "error")
                return
            }
            if (!form.valorDesconto) {
                alertStart("O campo valor do Desconto deve ser preenchido!", "error")
                return
            }
            if (form.tipoDesconto === "") {
                alertStart("O tipo do desconto deve ser informado!", "error")
                return
            }
        }

        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        const payload: SalvarMarketingPayload = {
            id: form.id,
            evento: form.evento,
            tempoPosEvento: form.tempoPosEvento ? Number(form.tempoPosEvento) : 0,
            tempoFinal: form.tempoFinal ? Number(form.tempoFinal) : null,
            diaParaEnviar: Number(form.diaParaEnviar),
            hora: form.hora,
            tituloMensagem: form.tituloMensagem,
            descricaoMensagem: form.descricaoMensagem,
            criarCupom: form.criarCupom,
        }

        if (form.criarCupom) {
            payload.nomeCupom = form.nomeCupom.trim().toUpperCase()
            payload.diasValidade = Number(form.diasValidade)
            payload.tipoDesconto = form.tipoDesconto === "true"
            payload.valorDesconto = Number(form.valorDesconto)
            payload.valorMinimo = form.valorMinimo ? Number(form.valorMinimo) : undefined
        }

        try {
            setSalvando(true)
            const response = await salvarMarketingAutomatico(autorizacao, payload)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao salvar estratégia", "error")
                return
            }
            dispatch({
                type: MarketingAutomaticoActionsTypes.ADICIONAR_OU_ATUALIZAR,
                payload: response,
            })
            alertStart("Estratégia salva com sucesso!", "success")
            onFechar()
        } finally {
            setSalvando(false)
        }
    }

    return (
        <Dialog open={aberto} onClose={onFechar} className={classes.dialog} maxWidth="md">
            <DialogTitle disableTypography>
                <Typography variant="h6" className={classes.titulo}>
                    {itemEditando
                        ? "Editar Estratégia"
                        : "Nova Estratégia de Marketing Automatizado"}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography className={classes.secao}>Gatilho</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Evento"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={form.evento}
                            onChange={e => {
                                const val = e.target.value
                                atualizarCampo("evento", val)
                            }}
                        >
                            {EVENTOS.map(op => (
                                <MenuItem key={op.value} value={op.value}>
                                    {op.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {!semDias && (
                        <>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    label="Dias após evento"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    value={form.tempoPosEvento}
                                    onChange={e => {
                                        const val = e.target.value
                                        atualizarCampo("tempoPosEvento", val)
                                    }}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    label="Até X dias"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    value={form.tempoFinal}
                                    onChange={e => {
                                        const val = e.target.value
                                        atualizarCampo("tempoFinal", val)
                                    }}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Dia da semana"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={form.diaParaEnviar}
                            onChange={e => {
                                const val = e.target.value
                                atualizarCampo("diaParaEnviar", val)
                            }}
                        >
                            {DIAS_SEMANA.map(op => (
                                <MenuItem key={op.value} value={op.value}>
                                    {op.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Hora do envio"
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="time"
                            value={form.hora}
                            onChange={e => {
                                const val = e.target.value
                                atualizarCampo("hora", val)
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography className={classes.secao}>Mensagem</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Título"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={form.tituloMensagem}
                            onChange={e => {
                                const val = e.target.value
                                atualizarCampo("tituloMensagem", val)
                            }}
                            inputProps={{ maxLength: 200 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Mensagem"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={3}
                            value={form.descricaoMensagem}
                            onChange={e => {
                                const val = e.target.value
                                atualizarCampo("descricaoMensagem", val)
                            }}
                            inputProps={{ maxLength: 2000 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography className={classes.secao}>Cupom</Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.criarCupom}
                                    onChange={e => {
                                        const checked = e.target.checked
                                        atualizarCampo("criarCupom", checked)
                                    }}
                                    color="primary"
                                />
                            }
                            label="Criar cupom de desconto"
                        />
                    </Grid>

                    {form.criarCupom && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nome do cupom"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={form.nomeCupom}
                                    onChange={e => {
                                        const val = e.target.value
                                        atualizarCampo("nomeCupom", val.toUpperCase())
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Validade em dias"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    value={form.diasValidade}
                                    onChange={e => {
                                        const val = e.target.value
                                        atualizarCampo("diasValidade", val)
                                    }}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Tipo de desconto"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={form.tipoDesconto}
                                    onChange={e => {
                                        const val = e.target.value
                                        atualizarCampo("tipoDesconto", val)
                                    }}
                                >
                                    <MenuItem value="false">Valor Fixo (R$)</MenuItem>
                                    <MenuItem value="true">Valor em Percentual (%)</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Valor do desconto"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    value={form.valorDesconto}
                                    onChange={e => {
                                        const val = e.target.value
                                        atualizarCampo("valorDesconto", val)
                                    }}
                                    inputProps={{ min: 0, step: "0.01" }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Valor mínimo do pedido (opcional)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    value={form.valorMinimo}
                                    onChange={e => {
                                        const val = e.target.value
                                        atualizarCampo("valorMinimo", val)
                                    }}
                                    inputProps={{ min: 0, step: "0.01" }}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end" mt={1} mb={1}>
                            <Button
                                variant="outlined"
                                className={classes.botaoCancelar}
                                onClick={onFechar}
                                disabled={salvando}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.botaoSalvar}
                                onClick={handleSalvar}
                                disabled={salvando}
                            >
                                {salvando ? "Salvando..." : "Salvar"}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}
